import type {
  ReportData,
  ReportType,
} from "./reportFamilyAdapters";
import {
  getReportTypeCounts,
  getSavedReportFields,
} from "./reportFamilyAdapters";

/**
 * Report Manager - LocalStorage-based persistence for uploaded dashboard reports
 *
 * Stores uploaded reports in browser LocalStorage for easy switching between
 * campaign, social, comparison, linear, streaming, and Hess dashboards.
 * Automatically cleans up reports older than 7 days.
 *
 * Features:
 * - Save/retrieve multiple reports
 * - Auto-cleanup of old reports
 * - Storage limit management
 * - Report metadata tracking
 */

const STORAGE_PREFIX = "campaign_report_";
const MAX_AGE_DAYS = 7;
const MAX_REPORTS = 20; // Keep at most 20 reports

export interface SavedReport {
  id: string;
  report_type: ReportType;
  uploaded_at: string;
  date_range_start: string;
  date_range_end: string;
  total_sales?: string; // Optional depending on report type
  total_spend?: string; // Optional depending on report type
  campaign_count?: number;
  brand_count?: number;
  channel_count?: number; // Linear
  data: ReportData;
  callouts?: string; // Optional user-added callouts/notes
  metadata?: {
    retailer?: string;
    client?: string;
    channel?: string;
    product?: string;
  }; // Retailer/client/channel/product metadata
}

type ReportMetadata = {
  retailer?: string;
  client?: string;
  channel?: string;
  product?: string;
};

type ReportStorageFailureReason =
  | "missing"
  | "expired"
  | "corrupt"
  | "storage_unavailable";

type ReportWriteFailureReason = ReportStorageFailureReason | "write_failed";

export type ReportReadResult =
  | { ok: true; report: SavedReport }
  | {
      ok: false;
      reason: ReportStorageFailureReason;
      error?: unknown;
      storageKey?: string;
    };

export type ReportListResult = {
  reports: SavedReport[];
  failures: Extract<ReportReadResult, { ok: false }>[];
};

export type ReportWriteResult =
  | { ok: true; report: SavedReport; storageKey: string }
  | { ok: true; reportId: string; storageKey: string }
  | {
      ok: false;
      reason: ReportWriteFailureReason;
      error?: unknown;
      retryError?: unknown;
      storageKey?: string;
    };

/**
 * Generate a unique ID for a report
 */
function generateReportId(): string {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get storage key for a report
 */
function getStorageKey(reportId: string): string {
  return `${STORAGE_PREFIX}${reportId}`;
}

function getReportStorageKeys(): string[] {
  const keys: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) {
      keys.push(key);
    }
  }

  return keys;
}

/**
 * Check if a report is expired (older than MAX_AGE_DAYS)
 */
function isReportExpired(uploadedAt: string): boolean {
  const uploadDate = new Date(uploadedAt);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - uploadDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > MAX_AGE_DAYS;
}

function parseStoredReport(data: string, storageKey: string): ReportReadResult {
  try {
    const report = JSON.parse(data) as SavedReport;

    if (isReportExpired(report.uploaded_at)) {
      return { ok: false, reason: "expired", storageKey };
    }

    return { ok: true, report };
  } catch (error) {
    return { ok: false, reason: "corrupt", error, storageKey };
  }
}

function removeFailedStoredReport(
  failure: Extract<ReportReadResult, { ok: false }>,
): void {
  if (
    failure.storageKey &&
    (failure.reason === "expired" || failure.reason === "corrupt")
  ) {
    localStorage.removeItem(failure.storageKey);
  }
}

function getStorageWriteFailureReason(error: unknown): ReportWriteFailureReason {
  if (
    error instanceof DOMException &&
    (error.name === "SecurityError" || error.name === "NotSupportedError")
  ) {
    return "storage_unavailable";
  }

  if (
    error instanceof Error &&
    (error.name === "SecurityError" || error.name === "NotSupportedError")
  ) {
    return "storage_unavailable";
  }

  return "write_failed";
}

function buildSavedReport(
  data: ReportData,
  metadata?: ReportMetadata,
): SavedReport {
  const reportId = generateReportId();

  const savedFields = getSavedReportFields(data);

  const savedReport: SavedReport = {
    id: reportId,
    report_type: data.report_type,
    uploaded_at: new Date().toISOString(),
    ...savedFields,
    data,
    metadata: metadata || {}, // Store retailer/client/channel/product metadata
  };

  return savedReport;
}

/**
 * Save a report to LocalStorage and return a structured persistence result.
 */
export function saveReportResult(
  data: ReportData,
  metadata?: ReportMetadata,
): ReportWriteResult {
  const savedReport = buildSavedReport(data, metadata);
  const storageKey = getStorageKey(savedReport.id);

  try {
    localStorage.setItem(storageKey, JSON.stringify(savedReport));

    // Cleanup old reports after saving
    cleanupOldReports();

    return { ok: true, report: savedReport, storageKey };
  } catch (error) {
    // If storage is full, try cleanup and retry
    try {
      cleanupOldReports();
      localStorage.setItem(storageKey, JSON.stringify(savedReport));
      return { ok: true, report: savedReport, storageKey };
    } catch (retryError) {
      const reason =
        getStorageWriteFailureReason(error) === "storage_unavailable"
          ? "storage_unavailable"
          : getStorageWriteFailureReason(retryError);
      return { ok: false, reason, error, retryError, storageKey };
    }
  }
}

/**
 * Save a report to LocalStorage
 */
export function saveReport(
  data: ReportData,
  metadata?: ReportMetadata,
): SavedReport {
  const result = saveReportResult(data, metadata);
  if (result.ok && "report" in result) {
    return result.report;
  }

  console.error("Failed to save report:", result);
  throw new Error("Storage full. Please delete some old reports.");
}

/**
 * Get a specific report by ID
 */
export function getReportResult(reportId: string): ReportReadResult {
  const storageKey = getStorageKey(reportId);

  try {
    const data = localStorage.getItem(storageKey);
    if (!data) {
      return { ok: false, reason: "missing", storageKey };
    }

    const result = parseStoredReport(data, storageKey);
    if (!result.ok) {
      removeFailedStoredReport(result);
    }
    return result;
  } catch (error) {
    return { ok: false, reason: "storage_unavailable", error, storageKey };
  }
}

export function getReport(reportId: string): SavedReport | null {
  const result = getReportResult(reportId);
  return result.ok ? result.report : null;
}

/**
 * Update callouts for a specific report
 */
export function updateReportCalloutsResult(
  reportId: string,
  callouts: string,
): ReportWriteResult {
  const result = getReportResult(reportId);
  if (!result.ok) {
    return result;
  }

  try {
    const report = result.report;
    report.callouts = callouts;
    const storageKey = getStorageKey(reportId);
    localStorage.setItem(storageKey, JSON.stringify(report));
    return { ok: true, report, storageKey };
  } catch (error) {
    return { ok: false, reason: getStorageWriteFailureReason(error), error };
  }
}

export function updateReportCallouts(
  reportId: string,
  callouts: string,
): boolean {
  return updateReportCalloutsResult(reportId, callouts).ok;
}

/**
 * Get all saved reports, sorted by upload date (newest first)
 */
export function getAllReportsResult(): ReportListResult {
  const reports: SavedReport[] = [];
  const failures: Extract<ReportReadResult, { ok: false }>[] = [];

  try {
    getReportStorageKeys().forEach((key) => {
      const data = localStorage.getItem(key);
      if (!data) {
        failures.push({ ok: false, reason: "missing", storageKey: key });
        return;
      }

      const result = parseStoredReport(data, key);
      if (result.ok) {
        reports.push(result.report);
      } else {
        removeFailedStoredReport(result);
        failures.push(result);
      }
    });
  } catch (error) {
    failures.push({ ok: false, reason: "storage_unavailable", error });
  }

  return {
    reports: reports.sort(
      (a, b) =>
        new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime(),
    ),
    failures,
  };
}

export function getAllReports(): SavedReport[] {
  const result = getAllReportsResult();

  result.failures.forEach((failure) => {
    if (failure.reason !== "missing" && failure.reason !== "expired") {
      console.error(
        "Failed to read report:",
        failure.storageKey,
        failure.reason,
        failure.error,
      );
    }
  });

  return result.reports;
}

export function deleteReportResult(reportId: string): ReportWriteResult {
  const storageKey = getStorageKey(reportId);

  try {
    localStorage.removeItem(storageKey);
    return { ok: true, reportId, storageKey };
  } catch (error) {
    return {
      ok: false,
      reason: getStorageWriteFailureReason(error),
      error,
      storageKey,
    };
  }
}

/**
 * Delete a specific report
 */
export function deleteReport(reportId: string): boolean {
  const result = deleteReportResult(reportId);
  if (!result.ok) {
    console.error("Failed to delete report:", result.error);
  }
  return result.ok;
}

/**
 * Clean up old reports (expired or exceeding MAX_REPORTS)
 */
export function cleanupOldReports(): number {
  let deletedCount = 0;
  const allReports: SavedReport[] = [];

  // First pass: collect all reports and delete expired ones
  getReportStorageKeys().forEach((key) => {
    try {
      const data = localStorage.getItem(key);
      if (data) {
        const report = JSON.parse(data) as SavedReport;

        // Check if expired and delete immediately
        if (isReportExpired(report.uploaded_at)) {
          localStorage.removeItem(key);
          deletedCount++;
        } else {
          allReports.push(report);
        }
      }
    } catch (error) {
      // Remove corrupted data
      localStorage.removeItem(key);
    }
  });

  // If still too many reports, delete oldest ones
  if (allReports.length > MAX_REPORTS) {
    // Sort by upload date, newest first
    allReports.sort(
      (a, b) =>
        new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime(),
    );

    const toDelete = allReports.slice(MAX_REPORTS);
    toDelete.forEach((report) => {
      deleteReport(report.id);
      deletedCount++;
    });
  }

  if (deletedCount > 0) {
    console.log(`🧹 Cleaned up ${deletedCount} old reports`);
  }

  return deletedCount;
}

/**
 * Get storage statistics
 */
export function getStorageStats(): {
  reportCount: number;
  searchCount: number;
  onsiteCount: number;
  offsiteCount: number;
  socialCount: number;
  linearCount: number;
  streamingCount: number;
  hessCount: number;
  comparisonCount: number;
  oldestReport: string | null;
  newestReport: string | null;
  estimatedSizeKB: number;
} {
  const reports = getAllReports();
  const counts = getReportTypeCounts(reports);

  return {
    reportCount: reports.length,
    searchCount: counts.search,
    onsiteCount: counts.onsite,
    offsiteCount: counts.offsite,
    socialCount: counts.social,
    linearCount: counts.linear,
    streamingCount: counts.streaming,
    hessCount: counts.hess,
    comparisonCount: counts.comparison,
    oldestReport:
      reports.length > 0 ? reports[reports.length - 1].uploaded_at : null,
    newestReport: reports.length > 0 ? reports[0].uploaded_at : null,
    estimatedSizeKB: Math.round(
      reports.reduce((sum, r) => sum + JSON.stringify(r).length, 0) / 1024,
    ),
  };
}

/**
 * Clear all reports (use with caution!)
 */
export function clearAllReports(): number {
  const keys = getReportStorageKeys();
  keys.forEach((key) => localStorage.removeItem(key));
  return keys.length;
}
